<script lang="ts" setup>
import { PageHeaderMenuItem } from '~/components/page/header/menu/PageHeaderMenu.vue'
import { ApiPagebuilder } from '~~/server/api/pagebuilder'

const route = useRoute()
const { pageHeaderMenu } = usePageHeaderMenu()
const { isLandscape } = useCheckDeviceOrientation()

const { data: pagebuilderComponents, redirect } = await useFetchWithRedirect<ApiPagebuilder>(
    '/api/pagebuilder',
    { route },
    {
        params: {
            slug: route.path,
            token: route.query.token || undefined,
        },
    }
)

if(redirect && !redirect?.error) {
    navigateTo(redirect.data.url, redirect.data.options)
}

// predefined layouts
const single_container = resolveComponent('PagebuilderSingleContainer')
const two_columns_container = resolveComponent('PagebuilderTwoColumnsContainer')
const three_columns_container = resolveComponent(
    'PagebuilderThreeColumnsContainer'
)
const one_third_columns_container = resolveComponent(
    'PagebuilderOneThirdColumnsContainer'
)
const two_third_columns_container = resolveComponent(
    'PagebuilderTwoThirdColumnsContainer'
)
const containers: { [key: string]: any } = {
    full: single_container,
    half: two_columns_container,
    three: three_columns_container,
    one_third: one_third_columns_container,
    two_third: two_third_columns_container,
}
// predefined components
const rich_editor = resolveComponent('PagebuilderRichEditor')
const media_finder = resolveComponent('PagebuilderMediaFinder')
const button = resolveComponent('PagebuilderButton')
const accordion = resolveComponent('PagebuilderAccordion')
const youtube = resolveComponent('PagebuilderYoutube')
const tabs = resolveComponent('PagebuilderTabs')
const slider = resolveComponent('PagebuilderSliderImages')
const gallery = resolveComponent('PagebuilderGallery')
const documents = resolveComponent('PagebuilderDocuments')
const plaintext = resolveComponent('PagebuilderPlaintext')
const contact_us = resolveComponent('PagebuilderContactUs')
const cards = resolveComponent('PagebuilderCardButton')
const faq = resolveComponent('PagebuilderFaq')
const Banner = resolveComponent('PagebuilderBanner')
const button_container = resolveComponent('PagebuilderButtonContainer')
const countdown = resolveComponent('PagebuilderCountdown')
const sticker = resolveComponent('PagebuilderSticker')
const form_component = resolveComponent('PagebuilderForm')
const cards_grand = resolveComponent('PagebuilderCardsGrand')
const card_gray = resolveComponent('PagebuilderCardGray')
const switch_component = resolveComponent('PagebuilderSwitch')

const components = shallowRef({
    rich_editor,
    media_finder,
    button,
    accordion,
    youtube,
    tabs,
    slider_component: slider,
    gallery,
    document_component: documents,
    plain_text: plaintext,
    contact_us,
    cards,
    faq,
    Banner,
    button_container,
    countdown,
    sticker,
    form_component,
    cards_grand,
    card_gray,
    switch_component,
})

const getContainer = (name: string) => containers[name]

const pageHeaderMenuItems = computed(() => {
    return pagebuilderComponents.value?.sticky_menu?.items?.map((item): PageHeaderMenuItem => ({ title: item.label, hash: `#${useSlug(item.uri)}` })) || []
})

const breadcrumbs = computed(() => {
    return pagebuilderComponents.value?.breadcrumbs?.map((item) => ({
        name: item.title,
        slug: item.slug,
    }))?.slice(0, -1)
})

const currentBreadcrumb = computed(() => {
    return pagebuilderComponents.value?.breadcrumbs?.length
        ? pagebuilderComponents.value.breadcrumbs[pagebuilderComponents.value.breadcrumbs.length - 1]?.title || 'Úvod'
        : 'Úvod'
})

const activeSection = ref(false)

const handleActiveSection = (section: boolean) => {
    activeSection.value = section
}

usePageMeta({
    meta: pagebuilderComponents.value?.meta,
    title: pagebuilderComponents.value?.title,
})
</script>
<template>
  <div v-if="pagebuilderComponents">
    <ContainerFluid>
      <Breadcrumbs
        :current="currentBreadcrumb"
        :links="
          breadcrumbs
            ||
            []"
        class="border-bottom pb-3 mb-4 mb-md-13"
      />
    </ContainerFluid>
    <PageHeader
      v-if="pagebuilderComponents.headline.image.path"
      :headline="pagebuilderComponents.headline"
      :title="pagebuilderComponents.title"
      :subtitle="pagebuilderComponents.headline.subtitle"
      :tag="pagebuilderComponents.headline.tag"
      :sections="pageHeaderMenuItems"
      @active-section="handleActiveSection($event)"
    />

    <PageHeaderContent
      v-else
      :title="pagebuilderComponents.title"
      :subtitle="pagebuilderComponents.headline.subtitle"
      :text-color="'black-100'"
    />

    <article
      class="mt-8 mb-8 px-2 px-md-0"
      :class="{ 'top-space-80': activeSection && pageHeaderMenu.show && !isLandscape }"
    >
      <Tracker
        v-for="(component, index) in pagebuilderComponents.jsonContent"
        :key="`cw-${index}`"
        :data="{ position: component.layout_settings.title }"
      >
        <component
          :is="getContainer(component._group)"
          :id="component._container_anchor"
          :components="components"
          :data="component.data"
          :layout-settings="component.layout_settings"
          :data-scroll-id="component._container_anchor ? component._container_anchor : null"
          class="scroll-margin-top-20 scroll-margin-top-md-10"
        />
      </Tracker>

      <IconsWrapper>
        <IconsSymbolExternal />
      </IconsWrapper>
    </article>
  </div>
</template>
